import React from 'react'
import { Typography, makeStyles } from '@material-ui/core'
import { Box, Card } from '@mui/material'
import { useSelector } from 'react-redux'
import { Letters } from '../constants/index'
import { round } from 'lodash'
import { colors } from 'utils/colors'

const OUTSIDE_LABEL_THRESHOLD = 12

const PollQuestion = ({ question }) => {
  const classes = useStyles()
  const { answers } = useSelector((state) => state.polling)

  const questionAnswers = answers.find((doc) => doc.questionId === question?.id)?.answers || []

  const optionsPercentage = question?.options?.map((option) => {
    const optionVotes = questionAnswers?.filter((doc) => doc?.optId === option?.optId)
    const optionCount = optionVotes?.length ?? 0
    const totalAnswers = questionAnswers?.length ?? 0
    return { optId: option.optId, vote: totalAnswers > 0 ? (optionCount * 100) / totalAnswers : 0 }
  })

  const getVotePercentageOfOption = (selectedOption) => {
    const foundOption = optionsPercentage.find(({ optId }) => optId === selectedOption)
    return foundOption ? round(foundOption?.vote, 0) : 0
  }

  const highestPercentage = Math.max(0, ...(optionsPercentage?.map(({ vote }) => vote) ?? [0]))

  return (
    <Card key={question?.id} elevation={0} className={classes.card}>
      <Typography className={classes.question}>{question?.title}</Typography>
      <Box className={classes.options}>
        {question?.options.map((option, idx) => {
          const optionPercentage = getVotePercentageOfOption(option?.optId)
          const isLeading = optionPercentage > 0 && optionPercentage === highestPercentage
          const labelInside = optionPercentage >= OUTSIDE_LABEL_THRESHOLD
          return (
            <Box key={option.optId} className={classes.optionRow}>
              <Box className={classes.optionLabelRow}>
                <span className={isLeading ? classes.indexChipLeading : classes.indexChip}>
                  {Letters[idx]}
                </span>
                <Typography className={classes.option}>{option.opt}</Typography>
              </Box>
              <Box className={classes.track}>
                <Box
                  className={isLeading ? classes.filledLeading : classes.filled}
                  style={{ width: `${optionPercentage}%` }}>
                  {labelInside && (
                    <Typography className={classes.percentageValue}>{optionPercentage}%</Typography>
                  )}
                </Box>
                {!labelInside && (
                  <Typography className={classes.percentageOutside}>{optionPercentage}%</Typography>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Card>
  )
}

export default PollQuestion

const useStyles = makeStyles((theme) => ({
  '@keyframes cardIn': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  card: {
    width: '600px',
    padding: '28px 36px',
    border: '1px solid rgba(15, 23, 42, 0.06)',
    borderRadius: '20px',
    height: 'fit-content',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 20px 45px -20px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.04)',
    animation: '$cardIn 0.4s ease',
    [theme.breakpoints.up('md')]: {
      width: '1000px'
    },
    [theme.breakpoints.up('lg')]: {
      width: '1200px'
    },
    [theme.breakpoints.up('xl')]: {
      width: '1300px'
    }
  },
  question: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#0f172a'
  },
  options: {
    marginTop: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '22px'
  },
  optionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  optionLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  indexChip: {
    flexShrink: 0,
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
    color: '#0e7a94',
    backgroundColor: '#e3f6fa',
    transition: 'background-color 0.4s ease, color 0.4s ease'
  },
  indexChipLeading: {
    flexShrink: 0,
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: colors.indigo,
    boxShadow: '0 4px 10px -2px rgba(21, 180, 219, 0.5)',
    transition: 'background-color 0.4s ease, color 0.4s ease'
  },
  option: {
    fontSize: '22px',
    color: '#1e293b'
  },
  track: {
    height: '50px',
    borderRadius: '10px',
    overflow: 'hidden',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f1f5f9'
  },
  filled: {
    height: '100%',
    minWidth: '2px',
    background: 'linear-gradient(90deg, #3ea8c2, #15b4db)',
    transition: 'width 0.7s cubic-bezier(0.4, 0, 0.2, 1), background 0.4s ease',
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center'
  },
  filledLeading: {
    height: '100%',
    minWidth: '2px',
    background: 'linear-gradient(90deg, #0e93b3, #22c9ef)',
    boxShadow: '0 0 14px rgba(21, 180, 219, 0.45)',
    transition: 'width 0.7s cubic-bezier(0.4, 0, 0.2, 1), background 0.4s ease',
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center'
  },
  percentageValue: {
    marginRight: '12px',
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#FFFFFF'
  },
  percentageOutside: {
    marginLeft: '10px',
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#64748b'
  }
}))
